import React, { useState, useEffect } from 'react';
import { 
  ArrowLeft, 
  Plus, 
  Edit, 
  Trash2, 
  Save, 
  X, 
  Database, 
  FileText,
  AlertCircle,
  Check,
  Search,
  ChevronDown,
  ChevronUp,
  Loader,
  RefreshCw,
  Eye,
  Settings,
  Filter,
  Download,
  Upload,
  Info
} from 'lucide-react';
import { DataService, CollectionSchema, SchemaField, PaginatedResult, SearchResult } from './services/DataService';
import { useAuth } from '../hooks/AuthProvider';

interface CollectionManagerProps {
  organizationId: string;
  collectionId: string;
  collectionName: string;
  onBack: () => void;
}

interface FormData {
  [key: string]: any;
}

interface ReferenceOption {
  id: string;
  name: string;
  displayText: string;
}

const CollectionManager: React.FC<CollectionManagerProps> = ({
  organizationId,
  collectionId,
  collectionName,
  onBack
}) => {
  const { currentUser } = useAuth();
  const [paginatedResult, setPaginatedResult] = useState<PaginatedResult<any> | null>(null);
  const [schema, setSchema] = useState<CollectionSchema | null>(null);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState<string>('');
  const [success, setSuccess] = useState<string>('');
  const [searchTerm, setSearchTerm] = useState<string>('');
  const [searchResult, setSearchResult] = useState<SearchResult | null>(null);
  const [isSearching, setIsSearching] = useState(false);
  const [searchFields, setSearchFields] = useState<string[]>([]);
  const [isFieldDropdownOpen, setIsFieldDropdownOpen] = useState(false);
  
  // New state for overview visibility
  const [showOverview, setShowOverview] = useState(false);
  
  // New state for filter functionality (column visibility)
  const [showFilters, setShowFilters] = useState(false);
  const [visibleColumns, setVisibleColumns] = useState<string[]>([]);
  
  // Modal states
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [editingDocument, setEditingDocument] = useState<any | null>(null);
  const [formData, setFormData] = useState<FormData>({});
  const [validationErrors, setValidationErrors] = useState<string[]>([]);
  const [submitting, setSubmitting] = useState(false);

  // NEW: State for the custom document ID field
  const [customDocumentId, setCustomDocumentId] = useState<string>('');

  // Reference field data cache
  const [referenceOptions, setReferenceOptions] = useState<Record<string, ReferenceOption[]>>({});
  const [loadingReferences, setLoadingReferences] = useState<Record<string, boolean>>({});

  // Pagination settings
  const DOCUMENTS_PER_PAGE = 20;

  useEffect(() => {
    loadInitialData();
  }, [organizationId, collectionId]);

  useEffect(() => {
    if (success) {
      const timer = setTimeout(() => setSuccess(''), 5000);
      return () => clearTimeout(timer);
    }
  }, [success]);

  useEffect(() => {
    if (error) {
      const timer = setTimeout(() => setError(''), 7000);
      return () => clearTimeout(timer);
    }
  }, [error]);

  // Load reference options when schema changes
  useEffect(() => {
    if (schema) {
      // Add a small delay to prevent duplicate calls in StrictMode
      const timeoutId = setTimeout(() => {
        loadReferenceOptions();
      }, 100);
      
      return () => clearTimeout(timeoutId);
    }
  }, [schema]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Element;
      if (!target.closest('.field-dropdown')) {
        setIsFieldDropdownOpen(false);
      }
    };

    if (isFieldDropdownOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [isFieldDropdownOpen]);

  // Load visible columns from localStorage when schema changes
  useEffect(() => {
    if (schema) {
      const storageKey = `${organizationId}_${collectionId}_filters`;
      const stored = localStorage.getItem(storageKey);
      
      if (stored) {
        try {
          const parsedColumns = JSON.parse(stored);
          setVisibleColumns(parsedColumns);
        } catch (error) {
          console.error('Error parsing stored column filters:', error);
          // Default to all columns if parsing fails
          setVisibleColumns(Object.keys(schema.fields));
        }
      } else {
        // Default to all columns
        setVisibleColumns(Object.keys(schema.fields));
      }
    }
  }, [schema, organizationId, collectionId]);

  // Save visible columns to localStorage whenever they change
  useEffect(() => {
    if (visibleColumns.length > 0) {
      const storageKey = `${organizationId}_${collectionId}_filters`;
      localStorage.setItem(storageKey, JSON.stringify(visibleColumns));
    }
  }, [visibleColumns, organizationId, collectionId]);

  // Helper function to recursively find all reference collections in a schema
  const findReferenceCollections = (fields: Record<string, SchemaField>): Set<string> => {
    const referenceCollections = new Set<string>();
    
    Object.entries(fields).forEach(([fieldName, fieldSchema]) => {
      if (fieldSchema.type === 'reference' && fieldSchema.referenceCollection) {
        referenceCollections.add(fieldSchema.referenceCollection);
      } else if (fieldSchema.type === 'array' && fieldSchema.arrayItemType) {
        // Check if array items are objects with reference fields
        if (fieldSchema.arrayItemType.type === 'object' && fieldSchema.arrayItemType.objectSchema) {
          const nestedCollections = findReferenceCollections(fieldSchema.arrayItemType.objectSchema);
          nestedCollections.forEach(col => referenceCollections.add(col));
        } else if (fieldSchema.arrayItemType.type === 'reference' && fieldSchema.arrayItemType.referenceCollection) {
          // Array of references
          referenceCollections.add(fieldSchema.arrayItemType.referenceCollection);
        }
      } else if (fieldSchema.type === 'object' && fieldSchema.objectSchema) {
        // Check nested object fields
        const nestedCollections = findReferenceCollections(fieldSchema.objectSchema);
        nestedCollections.forEach(col => referenceCollections.add(col));
      }
    });
    
    return referenceCollections;
  };

  const loadReferenceOptions = async () => {
    if (!schema) return;

    const referenceCollections = findReferenceCollections(schema.fields);
    const collectionsArray = Array.from(referenceCollections);
    
    console.log('Reference collections found:', collectionsArray);

    for (const collectionName of collectionsArray) {
      console.log(`Loading all documents from collection: ${collectionName}`);
      setLoadingReferences(prev => ({ ...prev, [collectionName]: true }));

      try {
        // Use searchDocuments with "*" to get all documents
        const result = await DataService.searchDocuments(
          organizationId,
          collectionName,
          ' ',
          { maxResults: 1000 }
        );

        console.log(`Loaded ${result.documents.length} documents from ${collectionName}:`, result.documents);

        const options: ReferenceOption[] = result.documents.map(doc => {
          // Try multiple possible name fields (case-insensitive)
          const nameField = doc.name || doc.Name || doc.title || doc.Title || doc.label || doc.Label;
          
          return {
            id: doc.id,
            name: nameField || doc.id,
            displayText: nameField || `Document ${doc.id}`
          };
        });

        console.log(`Created ${options.length} options for collection ${collectionName}:`, options);

        // Store options by collection name, not field path
        setReferenceOptions(prev => ({
          ...prev,
          [collectionName]: options
        }));
      } catch (err) {
        console.error(`Error loading reference options for collection ${collectionName}:`, err);
      } finally {
        setLoadingReferences(prev => ({ ...prev, [collectionName]: false }));
      }
    }
  };

  const loadInitialData = async () => {
    try {
      setLoading(true);
      
      // Load first page and schema in parallel
      const [initialResult, schemaData] = await Promise.all([
        DataService.getCollectionDocumentsPaginated(organizationId, collectionId, {
          documentsPerPage: DOCUMENTS_PER_PAGE,
          currentPage: 1  // NEW: explicitly request page 1
        }),
        DataService.getCollectionSchema(organizationId, collectionId)
      ]);
      
      setPaginatedResult(initialResult);
      setSchema(schemaData);
    } catch (err: any) {
      console.error('Error loading collection data:', err);
      setError(err.message || 'Failed to load collection data');
    } finally {
      setLoading(false);
    }
  };

  const loadMoreDocuments = async () => {
    if (!paginatedResult || !paginatedResult.hasMore || loadingMore) return;

    try {
      setLoadingMore(true);
      
      // Calculate next page
      const nextPage = (paginatedResult.currentPage || 1) + 1;
      
      // Load next page with previously loaded batches
      const moreResults = await DataService.getCollectionDocumentsPaginated(
        organizationId,
        collectionId,
        {
          documentsPerPage: DOCUMENTS_PER_PAGE,
          currentPage: nextPage,
          loadedBatches: paginatedResult.loadedBatches || []  // Pass cached batches
        }
      );
      
      // Combine new documents with existing ones
      const combinedResult = {
        ...moreResults,
        documents: [...paginatedResult.documents, ...moreResults.documents]
      };
      
      setPaginatedResult(combinedResult);
    } catch (err: any) {
      console.error('Error loading more documents:', err);
      setError(err.message || 'Failed to load more documents');
    } finally {
      setLoadingMore(false);
    }
  };

  const refreshData = async () => {
    try {
      setRefreshing(true);
      
      // Clear cache before refreshing - ADD THIS LINE
      DataService.clearCollectionCache(organizationId, collectionId);
      
      // Reload from the beginning
      const freshResult = await DataService.getCollectionDocumentsPaginated(
        organizationId,
        collectionId,
        { 
          documentsPerPage: DOCUMENTS_PER_PAGE,
          currentPage: 1  // Also add this for consistency
        }
      );
      
      setPaginatedResult(freshResult);
      setSearchResult(null);
      setSearchTerm('');
    } catch (err: any) {
      console.error('Error refreshing data:', err);
      setError(err.message || 'Failed to refresh data');
    } finally {
      setRefreshing(false);
    }
  };

  const handleSearch = async () => {
    if (!searchTerm.trim()) {
      // Clear search results if search term is empty
      setSearchResult(null);
      return;
    }

    try {
      setIsSearching(true);
      
      const result = await DataService.searchDocuments(
        organizationId,
        collectionId,
        searchTerm.trim(),
        {
          searchFields: searchFields.length > 0 ? searchFields : undefined,
          maxResults: 100
        }
      );
      
      setSearchResult(result);
    } catch (err: any) {
      console.error('Error searching documents:', err);
      setError(err.message || 'Failed to search documents');
    } finally {
      setIsSearching(false);
    }
  };

  const handleSearchKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      handleSearch();
    }
  };

  const clearSearch = () => {
    setSearchTerm('');
    setSearchResult(null);
    setSearchFields([]);
  };

  const toggleColumnVisibility = (fieldName: string) => {
    if (visibleColumns.includes(fieldName)) {
      setVisibleColumns(visibleColumns.filter(col => col !== fieldName));
    } else {
      setVisibleColumns([...visibleColumns, fieldName]);
    }
  };

  const selectAllColumns = () => {
    if (schema) {
      setVisibleColumns(Object.keys(schema.fields));
    }
  };

  const deselectAllColumns = () => {
    setVisibleColumns([]);
  };

  const resetForm = () => {
    if (schema) {
      setFormData(DataService.createDefaultFormData(schema));
    } else {
      setFormData({});
    }
    setValidationErrors([]);
    // NEW: Also reset the custom document ID
    setCustomDocumentId('');
  };

  const handleCreateDocument = async () => {
    if (!currentUser?.email) return;

    setSubmitting(true);
    setValidationErrors([]);

    try {
      // NEW: Use custom ID if provided, otherwise generate one
      const documentId = customDocumentId.trim() || DataService.generateDocumentId();
      
      // Prepare form data with document ID included
      const documentData = {
        id: documentId,
        ...formData
      };

      // Validate against schema if available
      if (schema) {
        const validation = await DataService.validateDocumentAgainstSchema(
          organizationId,
          collectionId,
          documentData
        );
        
        if (!validation.valid) {
          setValidationErrors(validation.errors);
          setSubmitting(false);
          return;
        }
      }

      await DataService.createDocument(organizationId, collectionId, documentId, documentData);
      
      setSuccess('Document created successfully');
      setIsCreateModalOpen(false);
      resetForm();

      DataService.clearCollectionCache(organizationId, collectionId);
      
      // Refresh data to show new document
      await refreshData();
    } catch (err: any) {
      console.error('Error creating document:', err);
      setError(err.message || 'Failed to create document');
    } finally {
      setSubmitting(false);
    }
  };

  const handleUpdateDocument = async () => {
    if (!editingDocument || !currentUser?.email) return;

    setSubmitting(true);
    setValidationErrors([]);

    try {
      // Prepare form data with document ID included
      const documentData = {
        id: editingDocument.id,
        ...formData
      };

      // Validate against schema if available
      if (schema) {
        const validation = await DataService.validateDocumentAgainstSchema(
          organizationId,
          collectionId,
          documentData
        );
        
        if (!validation.valid) {
          setValidationErrors(validation.errors);
          setSubmitting(false);
          return;
        }
      }

      await DataService.updateDocument(organizationId, collectionId, editingDocument.id, documentData);
      
      setSuccess('Document updated successfully');
      setEditingDocument(null);
      resetForm();

      DataService.clearCollectionCache(organizationId, collectionId);
      
      // Refresh data to show updated document
      await refreshData();
    } catch (err: any) {
      console.error('Error updating document:', err);
      setError(err.message || 'Failed to update document');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDeleteDocument = async (documentId: string) => {
    if (!currentUser?.email) return;
    
    if (!window.confirm('Are you sure you want to delete this document? This action cannot be undone.')) {
      return;
    }

    try {
      await DataService.deleteDocument(organizationId, collectionId, documentId);
      setSuccess('Document deleted successfully');

      DataService.clearCollectionCache(organizationId, collectionId);
      
      // Refresh data to remove deleted document
      await refreshData();
    } catch (err: any) {
      console.error('Error deleting document:', err);
      setError(err.message || 'Failed to delete document');
    }
  };

  const startEditing = (document: any) => {
    setEditingDocument(document);
    // Remove system fields and set form data
    const { id, batch, createdAt, updatedAt, ...editableData } = document;
    setFormData(editableData);
  };

  // Array table helpers for object arrays
  const addArrayTableRow = (fieldName: string, fieldSchema: SchemaField) => {
    const currentArray = formData[fieldName] || [];
    let newItem: any;
    
    if (fieldSchema.arrayItemType?.type === 'object' && fieldSchema.arrayItemType.objectSchema) {
      // Create object with default values for each field
      newItem = {};
      Object.entries(fieldSchema.arrayItemType.objectSchema).forEach(([subFieldName, subFieldSchema]) => {
        newItem[subFieldName] = DataService.getDefaultValueForField(subFieldSchema);
      });
    } else {
      newItem = fieldSchema.arrayItemType 
        ? DataService.getDefaultValueForField(fieldSchema.arrayItemType)
        : '';
    }
    
    setFormData({
      ...formData,
      [fieldName]: [...currentArray, newItem]
    });
  };

  const removeArrayTableRow = (fieldName: string, index: number) => {
    const currentArray = formData[fieldName] || [];
    const newArray = currentArray.filter((_: any, i: number) => i !== index);
    
    setFormData({
      ...formData,
      [fieldName]: newArray
    });
  };

  const updateArrayTableCell = (fieldName: string, rowIndex: number, subFieldName: string, value: any) => {
    const currentArray = formData[fieldName] || [];
    const newArray = [...currentArray];
    newArray[rowIndex] = { ...newArray[rowIndex], [subFieldName]: value };
    
    setFormData({
      ...formData,
      [fieldName]: newArray
    });
  };

  // Regular array helpers for primitive arrays
  const addArrayItem = (fieldName: string, fieldSchema: SchemaField) => {
    const currentArray = formData[fieldName] || [];
    const newItem = fieldSchema.arrayItemType 
      ? DataService.getDefaultValueForField(fieldSchema.arrayItemType)
      : '';
    
    setFormData({
      ...formData,
      [fieldName]: [...currentArray, newItem]
    });
  };

  const removeArrayItem = (fieldName: string, index: number) => {
    const currentArray = formData[fieldName] || [];
    const newArray = currentArray.filter((_: any, i: number) => i !== index);
    
    setFormData({
      ...formData,
      [fieldName]: newArray
    });
  };

  const updateArrayItem = (fieldName: string, index: number, value: any) => {
    const currentArray = formData[fieldName] || [];
    const newArray = [...currentArray];
    newArray[index] = value;
    
    setFormData({
      ...formData,
      [fieldName]: newArray
    });
  };

  const renderFormField = (fieldName: string, fieldSchema: SchemaField) => {
    const value = formData[fieldName] || '';

    const updateValue = (newValue: any) => {
      setFormData({ ...formData, [fieldName]: newValue });
    };

    const fieldId = `field-${fieldName}`;
    const isRequired = fieldSchema.required;
    const hasError = validationErrors.some(error => error.includes(fieldName));

    return (
      <div key={fieldName} className="space-y-2">
        <label 
          htmlFor={fieldId}
          className="block text-sm font-semibold text-gray-900"
        >
          {fieldName}
          {isRequired && <span className="text-red-500 ml-1">*</span>}
          {fieldSchema.description && (
            <span className="block text-xs font-normal text-gray-500 mt-1">{fieldSchema.description}</span>
          )}
        </label>
        
        {renderInputByType(fieldSchema, value, updateValue, fieldId, hasError, fieldName)}
        
        {hasError && (
          <p className="text-sm text-red-600 mt-1">
            {validationErrors.find(error => error.includes(fieldName))}
          </p>
        )}
      </div>
    );
  };

  const renderInputByType = (
    fieldSchema: SchemaField, 
    value: any, 
    updateValue: (value: any) => void,
    fieldId: string,
    hasError: boolean,
    fieldName: string
  ) => {
    const inputClassName = `w-full px-4 py-3 border-2 ${
      hasError ? 'border-red-300 focus:border-red-500' : 'border-gray-200 focus:border-blue-500'
    } rounded-lg focus:outline-none focus:ring-0 transition-colors text-gray-900 placeholder-gray-400`;

    switch (fieldSchema.type) {
      case 'string':
        return (
          <input
            id={fieldId}
            type="text"
            value={value}
            onChange={(e) => updateValue(e.target.value)}
            className={inputClassName}
            placeholder={fieldSchema.description || `Enter ${fieldName}`}
          />
        );

      case 'number':
        return (
          <input
            id={fieldId}
            type="number"
            value={value}
            onChange={(e) => updateValue(parseFloat(e.target.value) || 0)}
            className={inputClassName}
            placeholder={fieldSchema.description || `Enter ${fieldName}`}
          />
        );

      case 'boolean':
        return (
          <div className="flex items-center space-x-3">
            <label className="relative inline-flex items-center cursor-pointer">
              <input
                id={fieldId}
                type="checkbox"
                checked={value}
                onChange={(e) => updateValue(e.target.checked)}
                className="sr-only peer"
              />
              <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
              <span className="ml-3 text-sm text-gray-700">
                {fieldSchema.description || (value ? 'Yes' : 'No')}
              </span>
            </label>
          </div>
        );

      case 'date':
        return (
          <input
            id={fieldId}
            type="date"
            value={value}
            onChange={(e) => updateValue(e.target.value)}
            className={inputClassName}
          />
        );

      case 'reference':
        if (!fieldSchema.referenceCollection) {
          return (
            <input
              id={fieldId}
              type="text"
              value={value}
              onChange={(e) => updateValue(e.target.value)}
              className={inputClassName}
              placeholder="Reference ID"
            />
          );
        }

        // Use collection name as key instead of field path
        const options = referenceOptions[fieldSchema.referenceCollection] || [];
        const isLoadingOptions = loadingReferences[fieldSchema.referenceCollection];

        return (
          <div className="space-y-2">
            <select
              id={fieldId}
              value={value}
              onChange={(e) => updateValue(e.target.value)}
              className={inputClassName}
              disabled={isLoadingOptions}
            >
              <option value="">
                {isLoadingOptions ? 'Loading options...' : 'Select an option'}
              </option>
              {options.map(option => (
                <option key={option.id} value={option.id}>
                  {option.displayText}
                </option>
              ))}
            </select>
            <p className="text-xs text-gray-500">
              Reference to {fieldSchema.referenceCollection} collection
              {options.length > 0 && ` (${options.length} options)`}
            </p>
          </div>
        );

      case 'array':
        const arrayValue = Array.isArray(value) ? value : [];
        
        // Check if this is an array of objects that should be displayed as a table
        if (fieldSchema.arrayItemType?.type === 'object' && fieldSchema.arrayItemType.objectSchema) {
          const objectSchema = fieldSchema.arrayItemType.objectSchema;
          const columns = Object.keys(objectSchema);
          
          return (
            <div className="space-y-4 border-2 border-gray-200 rounded-lg p-4 bg-gray-50">
              <div className="flex items-center justify-between">
                <h4 className="text-sm font-semibold text-gray-900">
                  {fieldName} ({arrayValue.length} items)
                </h4>
                <button
                  type="button"
                  onClick={() => addArrayTableRow(fieldName, fieldSchema)}
                  className="inline-flex items-center px-4 py-2 bg-blue-600 text-white text-sm font-medium rounded-lg hover:bg-blue-700 transition-colors"
                >
                  <Plus size={16} className="mr-2" />
                  Add Row
                </button>
              </div>
              
              {arrayValue.length === 0 ? (
                <div className="text-center py-12 text-gray-500">
                  <Database className="w-12 h-12 mx-auto mb-4 text-gray-300" />
                  <p className="text-sm">No items added yet</p>
                  <p className="text-xs mt-1">Click "Add Row" to start</p>
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="min-w-full bg-white border border-gray-200 rounded-lg">
                    <thead className="bg-gray-100">
                      <tr>
                        {columns.map(columnName => (
                          <th key={columnName} className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider border-b">
                            {columnName}
                            {objectSchema[columnName].required && <span className="text-red-500 ml-1">*</span>}
                          </th>
                        ))}
                        <th className="px-4 py-3 text-center text-xs font-semibold text-gray-600 uppercase tracking-wider border-b w-20">
                          Actions
                        </th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-200">
                      {arrayValue.map((row: any, rowIndex: number) => (
                        <tr key={rowIndex} className="hover:bg-gray-50">
                          {columns.map(columnName => (
                            <td key={columnName} className="px-4 py-3 border-b">
                              {renderTableCell(
                                objectSchema[columnName],
                                row[columnName] || '',
                                (newValue) => updateArrayTableCell(fieldName, rowIndex, columnName, newValue),
                                `${fieldId}-${rowIndex}-${columnName}`
                              )}
                            </td>
                          ))}
                          <td className="px-4 py-3 text-center border-b">
                            <button
                              type="button"
                              onClick={() => removeArrayTableRow(fieldName, rowIndex)}
                              className="p-2 text-red-600 hover:text-red-800 hover:bg-red-50 rounded-lg transition-colors"
                              title="Remove row"
                            >
                              <Trash2 size={16} />
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          );
        }
        
        // Regular array of primitives
        return (
          <div className="space-y-3 border-2 border-gray-200 rounded-lg p-4 bg-gray-50">
            <div className="flex items-center justify-between">
              <span className="text-sm font-semibold text-gray-900">
                Items ({arrayValue.length})
              </span>
              <button
                type="button"
                onClick={() => addArrayItem(fieldName, fieldSchema)}
                className="inline-flex items-center px-3 py-1 bg-blue-600 text-white text-sm font-medium rounded-lg hover:bg-blue-700 transition-colors"
              >
                <Plus size={14} className="mr-1" />
                Add Item
              </button>
            </div>
            
            {arrayValue.length === 0 ? (
              <div className="text-center py-8 text-gray-500">
                <p className="text-sm">No items added yet</p>
              </div>
            ) : (
              <div className="space-y-2">
                {arrayValue.map((item: any, index: number) => (
                  <div key={index} className="flex items-center space-x-3 bg-white p-3 rounded-lg border border-gray-200">
                    <span className="text-sm font-medium text-gray-500 w-8">#{index + 1}</span>
                    <div className="flex-1">
                      {fieldSchema.arrayItemType ? (
                        renderInputByType(
                          fieldSchema.arrayItemType,
                          item,
                          (newValue) => updateArrayItem(fieldName, index, newValue),
                          `${fieldId}-item-${index}`,
                          false,
                          `${fieldName}[${index}]`
                        )
                      ) : (
                        <input
                          type="text"
                          value={item || ''}
                          onChange={(e) => updateArrayItem(fieldName, index, e.target.value)}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                          placeholder={`Item ${index + 1}`}
                        />
                      )}
                    </div>
                    <button
                      type="button"
                      onClick={() => removeArrayItem(fieldName, index)}
                      className="p-2 text-red-600 hover:text-red-800 hover:bg-red-50 rounded-lg transition-colors"
                      title="Remove item"
                    >
                      <Trash2 size={16} />
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>
        );

      case 'object':
        const objectValue = typeof value === 'object' && value !== null ? value : {};
        
        if (fieldSchema.objectSchema) {
          // Render object with defined schema as individual form fields
          return (
            <div className="space-y-4 border-2 border-gray-200 rounded-lg p-4 bg-gray-50">
              <h4 className="text-sm font-semibold text-gray-900">{fieldName} Properties</h4>
              {Object.entries(fieldSchema.objectSchema).map(([subFieldName, subFieldSchema]) => (
                <div key={subFieldName} className="bg-white p-4 rounded-lg border border-gray-200">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    {subFieldName}
                    {subFieldSchema.required && <span className="text-red-500 ml-1">*</span>}
                    {subFieldSchema.description && (
                      <span className="block text-xs text-gray-500 mt-1">{subFieldSchema.description}</span>
                    )}
                  </label>
                  {renderInputByType(
                    subFieldSchema,
                    objectValue[subFieldName],
                    (newValue) => {
                      const newObjectValue = { ...objectValue, [subFieldName]: newValue };
                      updateValue(newObjectValue);
                    },
                    `${fieldId}-${subFieldName}`,
                    false,
                    `${fieldName}.${subFieldName}`
                  )}
                </div>
              ))}
            </div>
          );
        } else {
          // Fallback for objects without schema - show key-value pairs
          const objectEntries = Object.entries(objectValue);
          
          return (
            <div className="space-y-3 border-2 border-gray-200 rounded-lg p-4 bg-gray-50">
              <div className="flex items-center justify-between">
                <span className="text-sm font-semibold text-gray-900">
                  Properties ({objectEntries.length})
                </span>
                <button
                  type="button"
                  onClick={() => {
                    const newKey = `property${objectEntries.length + 1}`;
                    updateValue({ ...objectValue, [newKey]: '' });
                  }}
                  className="inline-flex items-center px-3 py-1 bg-blue-600 text-white text-sm font-medium rounded-lg hover:bg-blue-700 transition-colors"
                >
                  <Plus size={14} className="mr-1" />
                  Add Property
                </button>
              </div>
              
              {objectEntries.length === 0 ? (
                <div className="text-center py-8 text-gray-500">
                  <p className="text-sm">No properties added yet</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {objectEntries.map(([key, val], index) => (
                    <div key={key} className="bg-white p-4 rounded-lg border border-gray-200">
                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <label className="block text-xs font-medium text-gray-600 mb-1">Property Name</label>
                          <input
                            type="text"
                            value={key}
                            onChange={(e) => {
                              const newKey = e.target.value;
                              const newObjectValue = { ...objectValue };
                              delete newObjectValue[key];
                              newObjectValue[newKey] = val;
                              updateValue(newObjectValue);
                            }}
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                            placeholder="Property name"
                          />
                        </div>
                        <div>
                          <label className="block text-xs font-medium text-gray-600 mb-1">Value</label>
                          <div className="flex items-center space-x-2">
                            <input
                              type="text"
                              value={typeof val === 'string' || typeof val === 'number' ? String(val) : ''}
                              onChange={(e) => {
                                updateValue({ ...objectValue, [key]: e.target.value });
                              }}
                              className="flex-1 px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                              placeholder="Property value"
                            />
                            <button
                              type="button"
                              onClick={() => {
                                const newObjectValue = { ...objectValue };
                                delete newObjectValue[key];
                                updateValue(newObjectValue);
                              }}
                              className="p-2 text-red-600 hover:text-red-800 hover:bg-red-50 rounded-lg transition-colors"
                              title="Remove property"
                            >
                              <Trash2 size={16} />
                            </button>
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          );
        }

      default:
        return (
          <input
            id={fieldId}
            type="text"
            value={value}
            onChange={(e) => updateValue(e.target.value)}
            className={inputClassName}
            placeholder={fieldSchema.description || `Enter ${fieldName}`}
          />
        );
    }
  };
  
  // Render table cell for array of objects
  const renderTableCell = (
    fieldSchema: SchemaField,
    value: any,
    updateValue: (value: any) => void,
    cellId: string
  ) => {
    const cellClassName = "w-full px-2 py-1 border-0 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-inset rounded text-sm";

    switch (fieldSchema.type) {
      case 'string':
        return (
          <input
            type="text"
            value={value || ''}
            onChange={(e) => updateValue(e.target.value)}
            className={cellClassName}
            placeholder="Enter text"
          />
        );

      case 'number':
        return (
          <input
            type="number"
            value={value || ''}
            onChange={(e) => updateValue(parseFloat(e.target.value) || 0)}
            className={cellClassName}
            placeholder="0"
          />
        );

      case 'boolean':
        return (
          <select
            value={value ? 'true' : 'false'}
            onChange={(e) => updateValue(e.target.value === 'true')}
            className={cellClassName}
          >
            <option value="false">No</option>
            <option value="true">Yes</option>
          </select>
        );

      case 'date':
        return (
          <input
            type="date"
            value={value || ''}
            onChange={(e) => updateValue(e.target.value)}
            className={cellClassName}
          />
        );

      case 'reference':
        if (!fieldSchema.referenceCollection) {
          return (
            <input
              type="text"
              value={value || ''}
              onChange={(e) => updateValue(e.target.value)}
              className={cellClassName}
              placeholder="Reference ID"
            />
          );
        }

        const options = referenceOptions[fieldSchema.referenceCollection] || [];
        const isLoadingOptions = loadingReferences[fieldSchema.referenceCollection];

        return (
          <select
            value={value || ''}
            onChange={(e) => updateValue(e.target.value)}
            className={cellClassName}
            disabled={isLoadingOptions}
          >
            <option value="">
              {isLoadingOptions ? 'Loading...' : 'Select'}
            </option>
            {options.map(option => (
              <option key={option.id} value={option.id}>
                {option.displayText}
              </option>
            ))}
          </select>
        );

      default:
        return (
          <input
            type="text"
            value={value || ''}
            onChange={(e) => updateValue(e.target.value)}
            className={cellClassName}
            placeholder="Enter value"
          />
        );
    }
  };

  // Render table cell content with special handling for arrays
  const renderTableCellContent = (value: any, fieldName: string, fieldSchema?: SchemaField) => {
    if (Array.isArray(value)) {
      return (
        <button
          onClick={() => {
            alert(`${fieldName} contains ${value.length} items:\n\n${JSON.stringify(value, null, 2)}`);
          }}
          className="inline-flex items-center px-2 py-1 bg-blue-100 text-blue-800 text-xs font-medium rounded-full hover:bg-blue-200 transition-colors"
        >
          Array ({value.length} items)
        </button>
      );
    }
    
    if (typeof value === 'object' && value !== null) {
      return (
        <button
          onClick={() => {
            alert(`${fieldName} object:\n\n${JSON.stringify(value, null, 2)}`);
          }}
          className="inline-flex items-center px-2 py-1 bg-purple-100 text-purple-800 text-xs font-medium rounded-full hover:bg-purple-200 transition-colors"
        >
          Object
        </button>
      );
    }
    
    // For primitive values, truncate if too long
    const stringValue = String(value || '');
    if (stringValue.length > 50) {
      return (
        <span title={stringValue} className="cursor-help">
          {stringValue.substring(0, 50)}...
        </span>
      );
    }
    
    return stringValue;
  };

  const renderDocumentCard = (document: any) => {
    const { id, batch, createdAt, updatedAt, ...displayData } = document;
    
    return (
      <div key={id} className="bg-white border border-gray-200 rounded-xl p-6 hover:shadow-lg transition-all duration-200 hover:border-gray-300">
        <div className="flex justify-between items-start mb-4">
          <div className="flex-1">
            <h3 className="text-lg font-semibold text-gray-900 mb-2">Document {id.split('_')[2]?.slice(-6) || id}</h3>
            <p className="text-sm text-gray-500">
              Created: {new Date(createdAt).toLocaleDateString('en-US', { 
                year: 'numeric', 
                month: 'short', 
                day: 'numeric',
                hour: '2-digit',
                minute: '2-digit'
              })}
            </p>
          </div>
          <div className="flex space-x-2">
            <button
              onClick={() => startEditing(document)}
              className="p-2 text-blue-600 hover:text-blue-800 hover:bg-blue-50 rounded-lg transition-colors"
              title="Edit document"
            >
              <Edit size={18} />
            </button>
            <button
              onClick={() => handleDeleteDocument(id)}
              className="p-2 text-red-600 hover:text-red-800 hover:bg-red-50 rounded-lg transition-colors"
              title="Delete document"
            >
              <Trash2 size={18} />
            </button>
          </div>
        </div>
        
        <div className="space-y-3">
          {Object.entries(displayData).map(([key, value]) => (
            <div key={key} className="flex justify-between items-start py-2 border-b border-gray-100 last:border-b-0">
              <span className="text-sm font-medium text-gray-600 capitalize min-w-0 flex-shrink-0 mr-4">
                {key}:
              </span>
              <span className="text-sm text-gray-900 break-words text-right">
                {Array.isArray(value) ? `Array (${value.length} items)` :
                 typeof value === 'object' ? 'Object' : 
                 String(value)}
              </span>
            </div>
          ))}
        </div>
      </div>
    );
  };

  // Get documents to display (search results or paginated results)
  const documentsToDisplay = searchResult?.documents || paginatedResult?.documents || [];
  const isDisplayingSearchResults = searchResult !== null;

  // Get field names in schema order, filtered by visible columns
  const getOrderedVisibleFields = () => {
    if (!schema) return [];
    return Object.keys(schema.fields).filter(fieldName => visibleColumns.includes(fieldName));
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-16 w-16 border-4 border-blue-600 border-t-transparent mx-auto mb-6"></div>
          <h2 className="text-2xl font-semibold text-gray-900 mb-2">Loading Collection</h2>
          <p className="text-gray-600">Please wait while we load your data...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100">
      {/* Header */}
      <div className="bg-white shadow-sm border-b border-gray-200">
        <div className="max-w-full mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-20">
            <div className="flex items-center">
              <button
                onClick={onBack}
                className="mr-6 p-3 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-all duration-200"
                title="Back to organization"
              >
                <ArrowLeft className="w-6 h-6" />
              </button>
              <div className="flex items-center">
                <div className="bg-blue-100 p-3 rounded-lg mr-4">
                  <Database className="w-8 h-8 text-blue-600" />
                </div>
                <div>
                  <h1 className="text-2xl font-bold text-gray-900">{collectionName}</h1>
                  <p className="text-sm text-gray-500">Collection ID: {collectionId}</p>
                </div>
              </div>
            </div>
            <div className="flex items-center space-x-4">
              {schema && (
                <span className="inline-flex items-center px-4 py-2 rounded-full text-sm font-medium bg-green-100 text-green-800">
                  <Check size={16} className="mr-2" />
                  Schema Defined
                </span>
              )}
              <button
                onClick={() => setShowOverview(!showOverview)}
                className="inline-flex items-center px-4 py-2 border border-gray-300 shadow-sm text-sm font-medium rounded-lg text-gray-700 bg-white hover:bg-gray-50 transition-colors"
              >
                <Info size={16} className="mr-2" />
                {showOverview ? 'Hide Overview' : 'Show Overview'}
              </button>
              <button
                onClick={refreshData}
                disabled={refreshing}
                className="inline-flex items-center px-4 py-2 border border-gray-300 shadow-sm text-sm font-medium rounded-lg text-gray-700 bg-white hover:bg-gray-50 transition-colors disabled:opacity-50"
              >
                <RefreshCw size={16} className={`mr-2 ${refreshing ? 'animate-spin' : ''}`} />
                Refresh
              </button>
              <button
                onClick={() => {
                  resetForm();
                  setIsCreateModalOpen(true);
                }}
                className="inline-flex items-center px-6 py-3 border border-transparent text-sm font-semibold rounded-lg text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 transition-all duration-200 shadow-lg hover:shadow-xl"
              >
                <Plus size={18} className="mr-2" />
                Add Document
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Alerts */}
      {error && (
        <div className="max-w-full mx-auto px-4 sm:px-6 lg:px-8 pt-6">
          <div className="bg-red-50 border-l-4 border-red-400 p-4 rounded-lg">
            <div className="flex">
              <AlertCircle className="w-5 h-5 text-red-400" />
              <div className="ml-3">
                <p className="text-sm text-red-800">{error}</p>
              </div>
              <button
                onClick={() => setError('')}
                className="ml-auto text-red-400 hover:text-red-600"
              >
                <X size={16} />
              </button>
            </div>
          </div>
        </div>
      )}

      {success && (
        <div className="max-w-full mx-auto px-4 sm:px-6 lg:px-8 pt-6">
          <div className="bg-green-50 border-l-4 border-green-400 p-4 rounded-lg">
            <div className="flex">
              <Check className="w-5 h-5 text-green-400" />
              <div className="ml-3">
                <p className="text-sm text-green-800">{success}</p>
              </div>
              <button
                onClick={() => setSuccess('')}
                className="ml-auto text-green-400 hover:text-green-600"
              >
                <X size={16} />
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Main Content */}
      <div className="max-w-full mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Collapsible Collection Overview Card */}
        {showOverview && (
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-8 mb-8">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-xl font-semibold text-gray-900">Collection Overview</h2>
              <div className="flex items-center space-x-8">
                <div className="text-center">
                  <p className="text-3xl font-bold text-blue-600">
                    {isDisplayingSearchResults ? searchResult!.totalResults : (paginatedResult?.totalCount || 0)}
                  </p>
                  <p className="text-sm text-gray-500 mt-1">
                    {isDisplayingSearchResults ? 'Search Results' : 'Items'}
                  </p>
                </div>
                {schema && (
                  <div className="text-center">
                    <p className="text-3xl font-bold text-green-600">{Object.keys(schema.fields).length}</p>
                    <p className="text-sm text-gray-500 mt-1">Schema Fields</p>
                  </div>
                )}
                {paginatedResult && !isDisplayingSearchResults && (
                  <div className="text-center">
                    <p className="text-3xl font-bold text-purple-600">
                      {paginatedResult.currentBatch}
                    </p>
                    <p className="text-sm text-gray-500 mt-1">Current Batch</p>
                  </div>
                )}
              </div>
            </div>
            
            {schema && (
              <div className="pt-6 border-t border-gray-200">
                <h3 className="text-lg font-medium text-gray-900 mb-4">Schema Fields</h3>
                <div className="flex flex-wrap gap-3">
                  {Object.entries(schema.fields).map(([fieldName, fieldSchema]) => (
                    <span
                      key={fieldName}
                      className={`inline-flex items-center px-4 py-2 rounded-full text-sm font-medium ${
                        fieldSchema.required 
                          ? 'bg-red-100 text-red-800 border border-red-200' 
                          : 'bg-gray-100 text-gray-800 border border-gray-200'
                      }`}
                    >
                      {fieldName} ({fieldSchema.type})
                      {fieldSchema.required && <span className="ml-1">*</span>}
                      {fieldSchema.type === 'reference' && fieldSchema.referenceCollection && (
                        <span className="ml-2 text-xs bg-blue-100 text-blue-800 px-2 py-1 rounded">→ {fieldSchema.referenceCollection}</span>
                      )}
                      {fieldSchema.type === 'array' && fieldSchema.arrayItemType && (
                        <span className="ml-2 text-xs bg-purple-100 text-purple-800 px-2 py-1 rounded">[{fieldSchema.arrayItemType.type}]</span>
                      )}
                    </span>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}

        {/* Data Table */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-8">
          <div className="flex items-center justify-between mb-8">
            <h2 className="text-xl font-semibold text-gray-900">
              {isDisplayingSearchResults ? 'Search Results' : 'Data'}
            </h2>
            <div className="flex items-center space-x-3">
              <button 
                onClick={() => setShowFilters(!showFilters)}
                className={`inline-flex items-center px-4 py-2 border shadow-sm text-sm font-medium rounded-lg transition-colors ${
                  showFilters 
                    ? 'border-blue-500 bg-blue-50 text-blue-700' 
                    : 'border-gray-300 bg-white text-gray-700 hover:bg-gray-50'
                }`}
              >
                <Filter size={16} className="mr-2" />
                Filter
              </button>
              <button className="inline-flex items-center px-4 py-2 border border-gray-300 shadow-sm text-sm font-medium rounded-lg text-gray-700 bg-white hover:bg-gray-50 transition-colors">
                <Download size={16} className="mr-2" />
                Export
              </button>
            </div>
          </div>

          {/* Filter Panel */}
          {showFilters && (
            <div className="mt-6 mb-8 bg-gray-50 border border-gray-200 rounded-lg p-6">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-medium text-gray-900">Column Visibility</h3>
                <div className="flex space-x-3">
                  <button
                    onClick={selectAllColumns}
                    className="text-sm text-blue-600 hover:text-blue-800 font-medium"
                  >
                    Select All
                  </button>
                  <button
                    onClick={deselectAllColumns}
                    className="text-sm text-gray-500 hover:text-gray-700 font-medium"
                  >
                    Deselect All
                  </button>
                </div>
              </div>
              
              {schema && (
                <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
                  {Object.entries(schema.fields).map(([fieldName, fieldSchema]) => (
                    <label key={fieldName} className="flex items-center space-x-3 p-3 bg-white rounded-lg border border-gray-200 hover:bg-gray-50 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={visibleColumns.includes(fieldName)}
                        onChange={() => toggleColumnVisibility(fieldName)}
                        className="rounded border-gray-300 text-blue-600 shadow-sm focus:border-blue-300 focus:ring focus:ring-blue-200 focus:ring-opacity-50"
                      />
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-gray-900 truncate">{fieldName}</p>
                        <p className="text-xs text-gray-500">{fieldSchema.type}</p>
                      </div>
                    </label>
                  ))}
                </div>
              )}
              
              <div className="mt-6 flex justify-between items-center">
                <p className="text-sm text-gray-600">
                  {visibleColumns.length} of {schema ? Object.keys(schema.fields).length : 0} columns visible
                </p>
                <button
                  onClick={() => setShowFilters(false)}
                  className="px-4 py-2 bg-blue-600 text-white rounded-lg text-sm font-medium hover:bg-blue-700 transition-colors"
                >
                  Done
                </button>
              </div>
            </div>
          )}

          {/* Enhanced Search Section - Now in main container */}
          <div className="space-y-6 mb-8">
            {/* Search Bar with Field Selector */}
            <div className="flex items-center space-x-4">
              {/* Multi-Select Field Dropdown */}
              {schema && Object.keys(schema.fields).length > 0 && (
                <div className="relative min-w-[200px] field-dropdown">
                  <button
                    type="button"
                    onClick={() => setIsFieldDropdownOpen(!isFieldDropdownOpen)}
                    className="flex items-center justify-between w-full px-4 py-4 border-2 border-gray-200 rounded-xl bg-white text-gray-900 focus:outline-none focus:border-blue-500 transition-colors hover:border-gray-300"
                  >
                    <span className="text-left">
                      {searchFields.length === 0 
                        ? 'All Fields' 
                        : searchFields.length === 1 
                          ? searchFields[0]
                          : `${searchFields.length} Fields Selected`
                      }
                    </span>
                    <ChevronDown className={`h-5 w-5 text-gray-400 transition-transform ${isFieldDropdownOpen ? 'rotate-180' : ''}`} />
                  </button>
                  
                  {isFieldDropdownOpen && (
                    <div className="absolute z-10 mt-2 w-full bg-white border border-gray-200 rounded-lg shadow-lg max-h-60 overflow-y-auto">
                      {/* All Fields Option */}
                      <label className="flex items-center px-4 py-3 hover:bg-gray-50 cursor-pointer border-b border-gray-100">
                        <input
                          type="checkbox"
                          checked={searchFields.length === 0}
                          onChange={(e) => {
                            if (e.target.checked) {
                              setSearchFields([]);
                            }
                          }}
                          className="rounded border-gray-300 text-blue-600 shadow-sm focus:border-blue-300 focus:ring focus:ring-blue-200 focus:ring-opacity-50"
                        />
                        <span className="ml-3 text-sm text-gray-900 font-medium">All Fields</span>
                      </label>
                      
                      {/* Individual Field Options */}
                      {Object.entries(schema.fields)
                        .filter(([_, fieldSchema]) => fieldSchema.type === 'string')
                        .map(([fieldName]) => (
                          <label key={fieldName} className="flex items-center px-4 py-3 hover:bg-gray-50 cursor-pointer">
                            <input
                              type="checkbox"
                              checked={searchFields.includes(fieldName)}
                              onChange={(e) => {
                                if (e.target.checked) {
                                  setSearchFields([...searchFields, fieldName]);
                                } else {
                                  setSearchFields(searchFields.filter(f => f !== fieldName));
                                }
                              }}
                              className="rounded border-gray-300 text-blue-600 shadow-sm focus:border-blue-300 focus:ring focus:ring-blue-200 focus:ring-opacity-50"
                            />
                            <span className="ml-3 text-sm text-gray-900">{fieldName}</span>
                          </label>
                        ))}
                    </div>
                  )}
                </div>
              )}
              
              {/* Search Input with Button */}
              <div className="relative flex-1 flex">
                <div className="relative flex-1">
                  <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                    <Search className="h-5 w-5 text-gray-400" />
                  </div>
                  <input
                    type="text"
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    onKeyPress={handleSearchKeyPress}
                    className="block w-full pl-12 pr-4 py-4 border-2 border-gray-200 rounded-l-xl leading-5 bg-white placeholder-gray-400 focus:outline-none focus:border-blue-500 focus:ring-0 transition-colors text-lg border-r-0"
                    placeholder={searchFields.length === 0 ? "Search all fields..." : `Search in ${searchFields.join(', ')}...`}
                  />
                </div>
                
                {/* Search Button */}
                <button
                  type="button"
                  onClick={handleSearch}
                  disabled={isSearching}
                  className="px-6 py-4 bg-blue-600 text-white border-2 border-blue-600 rounded-r-xl hover:bg-blue-700 hover:border-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center"
                >
                  {isSearching ? (
                    <Loader className="h-5 w-5 animate-spin" />
                  ) : (
                    <Search className="h-5 w-5" />
                  )}
                </button>
              </div>
            </div>

            {/* Search Results Info */}
            {isDisplayingSearchResults && (
              <div className="flex items-center justify-between bg-blue-50 border border-blue-200 rounded-lg p-4">
                <div>
                  <p className="text-sm text-blue-800">
                    Found <strong>{searchResult!.totalResults}</strong> results for "<strong>{searchResult!.searchTerm}</strong>"
                    {searchFields.length > 0 && (
                      <span> in fields: {searchFields.join(', ')}</span>
                    )}
                  </p>
                  {searchResult!.totalResults === 100 && (
                    <p className="text-xs text-blue-600 mt-1">
                      Results limited to 100. Try more specific search terms.
                    </p>
                  )}
                </div>
                <button
                  onClick={clearSearch}
                  className="text-sm text-blue-600 hover:text-blue-800 font-medium px-3 py-1 rounded-lg hover:bg-blue-100 transition-colors"
                >
                  Clear search
                </button>
              </div>
            )}
          </div>
          
          {documentsToDisplay.length === 0 ? (
            <div className="text-center py-16">
              <div className="bg-gray-100 rounded-full w-20 h-20 flex items-center justify-center mx-auto mb-6">
                <FileText className="w-10 h-10 text-gray-400" />
              </div>
              <h3 className="text-xl font-medium text-gray-900 mb-2">
                {isDisplayingSearchResults ? 'No search results' : 'No data yet'}
              </h3>
              <p className="text-gray-600 mb-8 max-w-sm mx-auto">
                {isDisplayingSearchResults 
                  ? 'Try adjusting your search terms or search fields to find what you\'re looking for.'
                  : 'Get started by adding your first item to this collection.'
                }
              </p>
              {!isDisplayingSearchResults && (
                <button
                  onClick={() => {
                    resetForm();
                    setIsCreateModalOpen(true);
                  }}
                  className="inline-flex items-center px-6 py-3 border border-transparent text-base font-medium rounded-lg text-white bg-blue-600 hover:bg-blue-700 transition-colors shadow-lg hover:shadow-xl"
                >
                  <Plus size={20} className="mr-2" />
                  Add First Item
                </button>
              )}
            </div>
          ) : (
            <>
              {/* Table View */}
              <div className="overflow-x-auto">
                <table className="min-w-full bg-white border border-gray-200 rounded-lg">
                  <thead className="bg-gray-50">
                    <tr>
                      {schema && getOrderedVisibleFields().map(fieldName => (
                        <th key={fieldName} className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider border-b">
                          {fieldName}
                        </th>
                      ))}
                      {!schema && documentsToDisplay.length > 0 && Object.keys(documentsToDisplay[0]).filter(key => !['id', 'batch', 'createdAt', 'updatedAt'].includes(key)).map(fieldName => (
                        <th key={fieldName} className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider border-b">
                          {fieldName}
                        </th>
                      ))}
                      <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider border-b w-24">
                        Actions
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {documentsToDisplay.map((document, index) => {
                      const { id, batch, createdAt, updatedAt, ...displayData } = document;
                      const fieldNames = schema ? getOrderedVisibleFields() : Object.keys(displayData);
                      
                      return (
                        <tr key={id} className="hover:bg-gray-50">
                          {fieldNames.map(fieldName => (
                            <td key={fieldName} className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 border-b">
                              {renderTableCellContent(displayData[fieldName], fieldName, schema?.fields[fieldName])}
                            </td>
                          ))}
                          <td className="px-6 py-4 whitespace-nowrap text-center text-sm font-medium border-b">
                            <div className="flex justify-center space-x-2">
                              <button
                                onClick={() => startEditing(document)}
                                className="p-2 text-blue-600 hover:text-blue-800 hover:bg-blue-50 rounded-lg transition-colors"
                                title="Edit"
                              >
                                <Edit size={16} />
                              </button>
                              <button
                                onClick={() => handleDeleteDocument(id)}
                                className="p-2 text-red-600 hover:text-red-800 hover:bg-red-50 rounded-lg transition-colors"
                                title="Delete"
                              >
                                <Trash2 size={16} />
                              </button>
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>

              {/* Load More Button for Pagination */}
              {!isDisplayingSearchResults && paginatedResult?.hasMore && (
                <div className="mt-12 text-center">
                  <button
                    onClick={loadMoreDocuments}
                    disabled={loadingMore}
                    className="inline-flex items-center px-8 py-4 border border-transparent text-base font-medium rounded-lg text-white bg-blue-600 hover:bg-blue-700 transition-colors disabled:opacity-50 shadow-lg hover:shadow-xl"
                  >
                    {loadingMore ? (
                      <>
                        <Loader size={20} className="mr-3 animate-spin" />
                        Loading Batch {paginatedResult.nextBatch}...
                      </>
                    ) : (
                      <>
                        <ChevronDown size={20} className="mr-3" />
                        Load More (Batch {paginatedResult.nextBatch})
                      </>
                    )}
                  </button>
                  <p className="mt-4 text-sm text-gray-500">
                    Showing {paginatedResult.totalCount} items from {
                      paginatedResult.currentBatch === paginatedResult.nextBatch ? 
                        `batch ${paginatedResult.currentBatch}` :
                        `batches ${paginatedResult.nextBatch! + 1}-${paginatedResult.currentBatch}`
                    }
                  </p>
                </div>
              )}
            </>
          )}
        </div>
      </div>

      {/* Full-Page Create/Edit Modal */}
      {(isCreateModalOpen || editingDocument) && (
        <div className="fixed inset-0 bg-white z-50" style={{height: "100vh", overflowY: "scroll"}}>
          <div className="min-h-screen">
            {/* Modal Header */}
            <div className="bg-white border-b border-gray-200 sticky top-0 z-10">
              <div className="max-w-full mx-auto px-4 sm:px-6 lg:px-8 py-6">
                <div className="flex items-center justify-between">
                  <div>
                    <h3 className="text-2xl font-bold text-gray-900">
                      {editingDocument ? 'Edit Item' : 'Create New Item'}
                    </h3>
                    <p className="text-gray-600 mt-1">
                      {editingDocument ? `Editing item ${editingDocument.id}` : `Adding a new item to ${collectionName}`}
                    </p>
                  </div>
                  <button
                    onClick={() => {
                      setIsCreateModalOpen(false);
                      setEditingDocument(null);
                      resetForm();
                      setValidationErrors([]);
                    }}
                    className="p-3 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
                  >
                    <X size={24} />
                  </button>
                </div>
              </div>
            </div>

            {/* Page Content */}
            <div className="max-w-full mx-auto px-4 sm:px-6 lg:px-8 py-8">
              {/* Validation Errors */}
              {validationErrors.length > 0 && (
                <div className="mb-8 p-4 bg-red-50 border-l-4 border-red-400 rounded-lg">
                  <div className="flex">
                    <AlertCircle className="w-5 h-5 text-red-400" />
                    <div className="ml-3">
                      <h3 className="text-sm font-medium text-red-800">Please fix the following errors:</h3>
                      <ul className="mt-2 text-sm text-red-700 list-disc list-inside space-y-1">
                        {validationErrors.map((error, index) => (
                          <li key={index}>{error}</li>
                        ))}
                      </ul>
                    </div>
                  </div>
                </div>
              )}

              {/* NEW: Custom Document ID Field - Only show when creating */}
              {isCreateModalOpen && !editingDocument && (
                <div className="mb-8 p-6 bg-blue-50 border border-blue-200 rounded-lg">
                  <h3 className="text-lg font-semibold text-blue-900 mb-4">Document ID (Optional)</h3>
                  <div className="space-y-2">
                    <label htmlFor="custom-document-id" className="block text-sm font-medium text-blue-800">
                      Custom Document ID
                    </label>
                    <input
                      id="custom-document-id"
                      type="text"
                      value={customDocumentId}
                      onChange={(e) => setCustomDocumentId(e.target.value)}
                      className="w-full px-4 py-3 border-2 border-blue-200 rounded-lg focus:outline-none focus:border-blue-500 focus:ring-0 transition-colors text-gray-900 placeholder-gray-400 bg-white"
                      placeholder="Leave empty to auto-generate an ID"
                    />
                    <p className="text-sm text-blue-600">
                      {customDocumentId.trim() 
                        ? `Document will be created with ID: ${customDocumentId.trim()}`
                        : 'If left empty, a unique ID will be automatically generated'
                      }
                    </p>
                  </div>
                </div>
              )}

              {/* Form Content */}
              {schema ? (
                <div className="space-y-8">
                  {Object.entries(schema.fields).map(([fieldName, fieldSchema]) =>
                    renderFormField(fieldName, fieldSchema)
                  )}
                </div>
              ) : (
                <div className="space-y-6">
                  <div className="text-center py-12 bg-gray-50 rounded-lg border-2 border-dashed border-gray-300">
                    <Settings className="w-12 h-12 mx-auto text-gray-400 mb-4" />
                    <h3 className="text-lg font-medium text-gray-900 mb-2">No Schema Defined</h3>
                    <p className="text-gray-600 mb-6 max-w-md mx-auto">
                      This collection doesn't have a schema. You can add any fields using JSON format.
                    </p>
                  </div>
                  <div>
                    <label className="block text-sm font-semibold text-gray-900 mb-2">
                      Document Data (JSON)
                    </label>
                    <textarea
                      value={JSON.stringify(formData, null, 2)}
                      onChange={(e) => {
                        try {
                          const parsed = JSON.parse(e.target.value);
                          setFormData(parsed);
                        } catch {
                          // Handle JSON parsing errors gracefully
                        }
                      }}
                      rows={20}
                      className="w-full px-4 py-3 border-2 border-gray-200 rounded-lg font-mono text-sm focus:outline-none focus:border-blue-500 focus:ring-0 transition-colors"
                      placeholder="Enter document data as JSON"
                    />
                  </div>
                </div>
              )}

              {/* Action Buttons - Now part of the normal page flow */}
              <div className="flex justify-end space-x-4 pt-12 mt-12 border-t border-gray-200">
                <button
                  onClick={() => {
                    setIsCreateModalOpen(false);
                    setEditingDocument(null);
                    resetForm();
                    setValidationErrors([]);
                  }}
                  className="px-6 py-3 border-2 border-gray-300 rounded-lg text-sm font-medium text-gray-700 hover:bg-gray-100 transition-colors"
                  disabled={submitting}
                >
                  Cancel
                </button>
                <button
                  onClick={editingDocument ? handleUpdateDocument : handleCreateDocument}
                  disabled={submitting}
                  className="inline-flex items-center px-8 py-3 border border-transparent text-sm font-semibold rounded-lg text-white bg-blue-600 hover:bg-blue-700 transition-colors disabled:opacity-50 shadow-lg hover:shadow-xl"
                >
                  <Save size={18} className="mr-2" />
                  {submitting 
                    ? (editingDocument ? 'Updating...' : 'Creating...') 
                    : (editingDocument ? 'Update Item' : 'Create Item')
                  }
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CollectionManager;